import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Filter,
  TrendingUp,
  Clock,
  Users,
  Target,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface ConversionFunnelProps {
  data: {
    funnelData: Array<{
      stage: string;
      count: number;
      value: number;
      conversionRate: number;
    }>;
    averageClosingTime: number;
    followUpRate: number;
    contactsPerBudget: number;
  };
  loading?: boolean;
}

function StageCard({
  stage,
  count,
  value,
  conversionRate,
  isLast = false,
}: {
  stage: string;
  count: number;
  value: number;
  conversionRate: number;
  isLast?: boolean;
}) {
  const getStageColor = (stageName: string) => {
    switch (stageName.toLowerCase()) {
      case 'rascunho':
        return 'bg-card text-foreground border-border';
      case 'enviado':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20';
      case 'aprovado':
        return 'bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20';
      default:
        return 'bg-card text-foreground border-border';
    }
  };

  const getProgressColor = (rate: number) => {
    if (rate >= 70) return 'bg-green-500';
    if (rate >= 40) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="relative">
      <Card className={cn('card-elevated', getStageColor(stage))}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">{stage}</CardTitle>
            {!isLast && (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-sm text-muted-foreground">
                {formatCurrency(value)}
              </div>
            </div>

            {!isLast && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span>Taxa de Conversão</span>
                  <span className="font-medium">{conversionRate.toFixed(1)}%</span>
                </div>
                <div className="relative">
                  <Progress
                    value={conversionRate}
                    className="h-2"
                    indicatorClassName={getProgressColor(conversionRate)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ConversionFunnel({ data, loading = false }: ConversionFunnelProps) {
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="h-20" />
                  <Skeleton className="h-8" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallConversionRate = data.funnelData.length > 1
    ? (data.funnelData[data.funnelData.length - 1].count / data.funnelData[0].count) * 100
    : 0;

  return (
    <div className="space-y-6">
      {/* Funnel Stages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Funil de Conversão
          </CardTitle>
          <CardDescription>
            Visualização do fluxo de conversão dos orçamentos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.funnelData.map((stage, index) => (
              <StageCard
                key={stage.stage}
                stage={stage.stage}
                count={stage.count}
                value={stage.value}
                conversionRate={stage.conversionRate}
                isLast={index === data.funnelData.length - 1}
              />
            ))}
          </div>

          {/* Overall Conversion Rate */}
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Taxa de Conversão Geral</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-finance-green">
                  {overallConversionRate.toFixed(1)}%
                </span>
                <Badge
                  variant={overallConversionRate >= 50 ? 'default' : 'secondary'}
                  className={cn(
                    overallConversionRate >= 50
                      ? 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
                      : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                  )}
                >
                  {overallConversionRate >= 50 ? 'Bom' : 'Precisa Melhorar'}
                </Badge>
              </div>
            </div>
            <div className="mt-2">
              <Progress
                value={overallConversionRate}
                className="h-3"
                indicatorClassName={overallConversionRate >= 50 ? 'bg-green-500' : 'bg-amber-500'}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversion Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio de Fechamento</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {data.averageClosingTime} dias
            </div>
            <p className="text-xs text-muted-foreground">
              Do envio à aprovação
            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Follow-up</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-finance-green">
              {data.followUpRate}%
            </div>
            <p className="text-xs text-muted-foreground">
              Orçamentos com acompanhamento
            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Contatos por Orçamento</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {data.contactsPerBudget}
            </div>
            <p className="text-xs text-muted-foreground">
              Média de interações
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Insights de Conversão</CardTitle>
          <CardDescription>
            Análise e recomendações para melhorar a taxa de conversão
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Ponto de Atenção</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {overallConversionRate < 50
                  ? 'A taxa de conversão está abaixo do ideal. Considere revisar o processo de follow-up.'
                  : overallConversionRate < 70
                  ? 'Boa taxa de conversão, mas ainda há espaço para melhorias.'
                  : 'Excelente taxa de conversão! Mantenha os processos atuais.'
                }
              </p>
            </div>

            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">Recomendação</h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                {data.averageClosingTime > 15
                  ? 'Reduza o tempo de resposta para aumentar as chances de aprovação.'
                  : 'O tempo de fechamento está bom. Foco na qualidade do atendimento.'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}